import { useCallback, useMemo, useState } from 'react'

import { Grid } from '@mantine/core'

import { useQuery } from '@redwoodjs/web'

import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_CALL_STATUSES_METRICS } from 'src/graphql/callAnaltyics.graphql'
import { useRefetchOnKeyChange } from 'src/pages/AnalyticsPage/components/trendChartHelpers'

import AnalyticsCard from '../../AnalyticsPage/components/AnalyticsCard'
import { CallActivityByEmployeeContainer } from '../components/CallActivityByEmployeeContainer'
import CallCoverageHeatmap, {
  CALL_COVERAGE_INFO_TEXT,
} from '../components/CallCoverage/CallCoverageHeatmap'
import CallDurationStats from '../components/CallDurationStats'
import {
  CallStatusSummaryCard,
  CALL_STATUS_LABELS,
} from '../components/CallStatusSummaryCard'
import { CallVolumeContainer } from '../components/CallVolumeContainer'
import FiltersBar from '../components/FiltersBar'
import { FiltersPortal } from '../components/FiltersPortal'
import { generateMetricsByDateRange, getUrlParams } from '../utils'

export interface CallAnalyticsTabProps {
  /**
   * Bumped by the page-level Refresh button. Threaded through to
   * containers (e.g. CallVolumeContainer) that own their own useQuery so
   * they can call refetch() without us re-keying the whole tab — which
   * would reset local state like the Call Volume bucket toggle.
   */
  refreshKey: number
}

const CallAnalyticsTab = ({ refreshKey }: CallAnalyticsTabProps) => {
  // Read URL params once at mount via lazy useState initializers — repeated
  // reads would re-parse window.location on every render for no benefit.
  const [startDate, setStartDate] = useState(
    () => getUrlParams().defaultStartDate
  )
  const [endDate, setEndDate] = useState(() => getUrlParams().defaultEndDate)
  const [callTypes, setCallTypes] = useState(
    () => getUrlParams().defaultCallTypes
  )
  const [selectedConnectedStatus, setSelectedConnectedStatus] = useState(
    () => getUrlParams().defaultConnectedStatus
  )
  const [callStatuses, setCallStatuses] = useState(
    () => getUrlParams().defaultCallStatuses
  )
  const [selectedRecruiters, setSelectedRecruiters] = useState(
    () => getUrlParams().defaultRecruiters
  )
  const [callDirections, setCallDirections] = useState(
    () => getUrlParams().defaultCallDirections
  )
  const [callStatusDailyMetrics, setCallStatusDailyMetrics] = useState([])

  const onError = useCallback(() => {
    toast('Something went wrong, please try again.', 'error')
  }, [])

  // The same filter shape is consumed by every query + child component on
  // this tab. Memoizing once keeps the queries' `variables` referentially
  // stable across re-renders and removes 30+ lines of duplication below.
  const filters = useMemo(
    () => ({
      connectedStatuses: selectedConnectedStatus,
      recruiters: selectedRecruiters,
      callDirections,
      callStatuses,
      callTypes,
      dateRange: { gte: startDate, lte: endDate },
    }),
    [
      selectedConnectedStatus,
      selectedRecruiters,
      callDirections,
      callStatuses,
      callTypes,
      startDate,
      endDate,
    ]
  )

  const { loading: callStatusesMetricsLoading, refetch: refetchCallStatuses } =
    useQuery(GET_CALL_STATUSES_METRICS, {
      variables: { filters },
      onError,
      notifyOnNetworkStatusChange: true,
      onCompleted: (data) => {
        setCallStatusDailyMetrics(
          generateMetricsByDateRange(
            startDate,
            endDate,
            data?.callStatusesMetrics || [],
            CALL_STATUS_LABELS,
            'callStatus'
          )
        )
      },
    })

  useRefetchOnKeyChange(refetchCallStatuses, refreshKey)

  return (
    <>
      <FiltersPortal>
        <FiltersBar
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          callDirections={callDirections}
          setCallDirections={setCallDirections}
          selectedRecruiters={selectedRecruiters}
          setSelectedRecruiters={setSelectedRecruiters}
          callTypes={callTypes}
          setCallTypes={setCallTypes}
          callStatuses={callStatuses}
          setCallStatuses={setCallStatuses}
          selectedConnectedStatus={selectedConnectedStatus}
          setSelectedConnectedStatus={setSelectedConnectedStatus}
        />
      </FiltersPortal>
      <Grid gutter="xs" className="h-full overflow-y-auto overflow-x-hidden">
        <Grid.Col span={4}>
          <CallStatusSummaryCard
            dailyMetrics={callStatusDailyMetrics}
            loading={callStatusesMetricsLoading}
          />
        </Grid.Col>

        <Grid.Col span={8}>
          <CallVolumeContainer filters={filters} refreshKey={refreshKey} />
        </Grid.Col>
        <Grid.Col span={12}>
          <AnalyticsCard
            title="Call Coverage"
            showBadge={false}
            description="When calls happen and who covers each hour against staffed hours"
            info={CALL_COVERAGE_INFO_TEXT}
          >
            <CallCoverageHeatmap filters={filters} refreshKey={refreshKey} />
          </AnalyticsCard>
        </Grid.Col>
        <Grid.Col span={8}>
          <CallActivityByEmployeeContainer
            filters={filters}
            refreshKey={refreshKey}
          />
        </Grid.Col>
        <Grid.Col span={4}>
          <AnalyticsCard title="Call Duration" description="Total duration">
            <CallDurationStats filters={filters} />
          </AnalyticsCard>
        </Grid.Col>
      </Grid>
    </>
  )
}

export default CallAnalyticsTab
